// src/pages/Reports.js
import React, { useState } from 'react';
import axios from 'axios';
import ExportButton from '../components/ExportButton';

function Reports() {
  const [reportType, setReportType] = useState('daily');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let params = {};
      
      if (reportType === 'daily') {
        endpoint = `${API_URL}/reports/daily`;
        params = { date };
      } else {
        endpoint = `${API_URL}/reports/weekly`;
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(d.setDate(diff));
        params = { week_start: monday.toISOString().split('T')[0] };
      }
      
      const response = await axios.get(endpoint, {
        params,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      setReport(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const getExportData = () => {
    if (!report) return [];
    
    if (reportType === 'daily') {
      return [{
        'Date': report.date,
        'Total Rides': report.summary.total_rides,
        'Completed': report.summary.completed_rides,
        'Cancelled': report.summary.cancelled_rides,
        'Revenue': report.summary.total_revenue,
        'Avg Rating': report.summary.avg_rating,
        'Active Drivers': report.summary.active_drivers
      }];
    } else {
      const data = report.daily_data?.map(day => ({
        'Date': day.date,
        'Rides': day.rides,
        'Completed': day.completed,
        'Revenue': day.revenue
      })) || [];
      
      data.push({
        'Date': 'TOTAL',
        'Rides': report.summary.total_rides,
        'Completed': '-',
        'Revenue': report.summary.total_revenue
      });
      
      return data;
    }
  };

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>📊 Reports</h1>
      </div>

      {/* Report Controls */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
      }}>
        <div>
          <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '14px',
            }}
          >
            <option value="daily">Daily Report</option>
            <option value="weekly">Weekly Report</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
            {reportType === 'daily' ? 'Date' : 'Week Starting'}
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '14px',
            }}
          />
        </div>
        <button
          onClick={generateReport}
          disabled={loading}
          style={{
            padding: '10px 24px',
            backgroundColor: '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
        {report && (
          <ExportButton 
            data={getExportData()} 
            filename={`${reportType}_report_${date}`} 
            label="Export CSV"
          />
        )}
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Report Display */}
      {report && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>
            {reportType === 'daily' ? 'Daily Report' : 'Weekly Report'}
            <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b', marginLeft: '12px' }}>
              {reportType === 'daily' ? report.date : `${report.week_start} to ${report.week_end}`}
            </span>
          </h3>

          {reportType === 'daily' ? (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px',
                marginBottom: '20px',
              }}>
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Rides</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f97316' }}>{report.summary.total_rides}</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Completed</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>{report.summary.completed_rides}</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Revenue</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>UGX {report.summary.total_revenue.toLocaleString()}</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Avg Rating</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>⭐ {report.summary.avg_rating.toFixed(1)}</div>
                </div>
              </div>

              {report.top_drivers && report.top_drivers.length > 0 && (
                <div>
                  <h4 style={{ marginBottom: '12px' }}>🏆 Top Drivers</h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Driver</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Rides</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Earnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.top_drivers.map((driver, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px' }}>
                            {index === 0 && '🥇 '}
                            {index === 1 && '🥈 '}
                            {index === 2 && '🥉 '}
                            {driver.driver_name}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{driver.rides_completed}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>
                            UGX {driver.earnings.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px',
                marginBottom: '20px',
              }}>
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Rides</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f97316' }}>{report.summary.total_rides}</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Revenue</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>UGX {report.summary.total_revenue.toLocaleString()}</div>
                </div>
              </div>

              <h4 style={{ marginBottom: '12px' }}>Daily Breakdown</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>Rides</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>Completed</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {report.daily_data?.map((day, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px' }}>{new Date(day.date).toLocaleDateString()}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{day.rides}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{day.completed}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>
                        UGX {day.revenue.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Reports;